// our stuff:
import { createDivElement } from './utils';
import EventEmitter from 'eventemitter3';
import { Panel } from './Panels/Panel';

interface PanelVisibilityChangeEvent {
    /* ID of Panel that changed visibility. */
    panel_id: string;
}

interface PanelContainerEventMap {
    "panelShow": PanelVisibilityChangeEvent;
    "panelHide": PanelVisibilityChangeEvent;
}

/**
 * @class PanelContainer 
 * @brief Element to hold multiple {@link Panel} object.
 */
abstract class PanelContainer<EventTypes extends string | symbol = string | symbol> extends EventEmitter<keyof PanelContainerEventMap | EventTypes> {
    /** @brief List of registered panels. */
    protected _panels: Panel[];

    /** @brief Element to attach to. Also PanelContainer's z-index is calculated to be above this element. */
    protected parentElement: HTMLElement;

    /** @brief If true, shadow is displayed (and automatically created) under rendered PanelContainer.
     *
     * Default is false.
     */
    protected enabledShadow: boolean;

    /** @brief Array holding list of Panel IDs that should be shown when temporary mode is switched off. */
    protected visiblePanelIds: string[];

    /** @brief Main element holding all of PanelContainer's content. Have "*PREFIX*" class. */
    readonly container: HTMLElement;

    /** @brief Element holding panels themselves. Have "*PREFIX*-content" class. */
    readonly content: HTMLElement;

    /** @brief Element holding "shadow" (aka "backdrop") element. */
    readonly shadow: HTMLElement;

    /** @brief Creates instance of PanelContainer.
     * @param     {HTMLElement} parentElement Element to which to append container element.
     * @param     {string} baseClass Class prefix for element.
     */
    constructor(parentElement: HTMLElement, baseClass: string, backgroundClass: string) {
        super();

        this.enabledShadow = false;

        // public:
        this.parentElement = parentElement;
        this._panels = [];
        this.visiblePanelIds = [];

        // elements:
        this.container = createDivElement(this.parentElement, baseClass + ' hidden');
       
        this.content = createDivElement(this.container, baseClass + '-content');
        if (backgroundClass)
            this.content.classList.add(backgroundClass);

        this.shadow = createDivElement(null, 'shadow5');
        this.shadow.onclick = this.hide.bind(this);
        this.shadow.oncontextmenu = (ev) => this.shadow.onclick(ev);
    }

    /** @brief Flag enabling/disabling shadow for container. */
    get enableShadow() {
        return this.enabledShadow;
    }
    set enableShadow(enableShadow: boolean) {
        this.enabledShadow = enableShadow;

        if (this.enabledShadow)
            this._createShadow();
        else
            this._destroyShadow();
    }

    /** @brief Creates shadow element for container.
     * @param {HTMLElement} [shadowedElement] Z-index of created shadow will be calculated to be on top of this element
     */
    protected _createShadow(shadowedElement: HTMLElement = this.container) {
        // get z-index of current element:
        const style = window.getComputedStyle(shadowedElement, null);
        let zIndex = 0;
        if (style) {
            zIndex = Number.parseInt(style.getPropertyValue('z-index'), 10);
        }

        // check if shadow is already there
        if (this.shadow.parentElement)
            return;

        // create if needed:
        this.shadow.style.zIndex = (zIndex - 1).toString();
        document.body.appendChild(this.shadow);
    }

    /** @brief Destroys shadow element for container. */
    protected _destroyShadow(): void {
        // check if shadow is already there
        if (!this.shadow.parentElement)
            return;

        this.shadow.remove();
    }

    /** @brief Internal callback for Panels to call when they become visible. */
    _onPanelShow(panel: Panel) : void {
        this.emit('panelShow', { panel_id: panel.id });
    }

    /** @brief Internal callback for Panels to call when they become hidden. */
    _onPanelHide(panel: Panel) : void {
        this.emit('panelHide', { panel_id: panel.id });
    }

    /** @brief Shows container. */
    show(): this {
        this.container.classList.remove('hidden');
        return this;
    }

    /** @brief Hides container completely. */
    hide(): this {
        this.container.classList.add('hidden');
        return this;
    }

    /** @brief Signals to panels that they need to update themself.
     * @param     {unknown} newState Object containing new state (can be anything).
     * @param     {unknown} oldState Object containing old state (can be anything).
     * @param     {string[]} [affectedPanels] List of panel IDs to receive update.
     * @returns   {boolean}
     */
    update(newState: unknown, oldState: unknown, affectedPanels?: (string | [string, boolean])[]): boolean {
        const filterPanels: string[] = [];
        if (Array.isArray(affectedPanels)) for (const panel_id of affectedPanels) {
            if (Array.isArray(panel_id))
                filterPanels.push(panel_id[0])
            else
                filterPanels.push(panel_id)
        }
        
        for (const panel of this._panels) {
            if (!filterPanels.includes(panel.id))
                continue;
            if (typeof panel.update === "function")
                panel.update(newState, oldState);
        }

        return true;
    }

    /** @brief Detaches panel from parent. */
    destroy() {
        this.container.remove();
    }

    /** @brief Checks if panel with given id is already in container.
     * @param {string} panel_id ID of Panel to check for existence.
     * @returns {boolean} True if panel with given id is already in container, false otherwise.
     */
    includes(panel_id: string): boolean {
        return this._panels.some(panel => panel.id === panel_id);
    }

    /** @brief Adds panel to container.
     * @param     {Panel} panel Panel object to add to container.
     * @returns   {void}
     */
    addPanel(panel: Panel): void {
        if (this.includes(panel.id))
            return;

        panel.parentObj = this;
        this._panels.push(panel);
        this.content.appendChild(panel.panelElement);

        if (!panel.isHidden()) {
            this.visiblePanelIds.push(panel.id);
        }
    }

    /** @brief Adds panel to container.
     * @param     {Panel} predecessorPanel Panel object that precedes new panel.
     * @param     {Panel} newPanel Panel object to add to container.
     * @returns   {void}
     */
    insertPanelAfter(predecesserPanelId: string | undefined | null, newPanel: Panel): void {
        if (this.includes(newPanel.id))
            return;

        newPanel.parentObj = this;

        const pos = (!predecesserPanelId || predecesserPanelId === '') ? 0 : this._panels.findIndex((e) => e.id === predecesserPanelId) + 1;
        this._panels.splice(pos, 0, newPanel);

        let refPanelElement = null;
        if (predecesserPanelId && predecesserPanelId !== '') {
            const predecesserPanelElement = this.content.querySelector<HTMLElement>('#' + predecesserPanelId);
            if (predecesserPanelElement) {
                refPanelElement = predecesserPanelElement.nextSibling;
            }
        }
        this.content.insertBefore(newPanel.panelElement, refPanelElement);

        if (!newPanel.isHidden()) {
            this.visiblePanelIds.push(newPanel.id);
        }
    }

    /** @brief Gets panel object by it's ID.
     * @deprecated
     * @param     {string} panel_id ID of Panel to get.
     * @returns   {Panel} Found Panel object or undefined if no Panel was found.
     */
    getPanel(panel_id: string): Panel {
        for (const panel of this._panels) {
            if (panel.id === panel_id)
                return panel;
        }

        return undefined;
    }

    /** @brief Gets panels id array.
     * @deprecated
     * @returns   {string[]} Array of panels id or empty one.
     */
     getAllId(): string[] {
        const panelsId: string[] = [];
        for (const panel of this._panels)
            panelsId.push(panel.id);

        return panelsId;
    }

    /** @brief Removes panel from container by it's ID.
     * @param     {string} panel_id ID of Panel to remove.
     * @returns   {boolean} True if panel was removed, false if no panel with requested ID was found.
     */
    removePanel(panel_id: string): Panel {
        for (let i = 0; i < this._panels.length; i++) {
            const panel=this._panels[i];
            if (panel.id !== panel_id) continue;

            if (panel.panelElement.parentElement === this.content)
                panel.panelElement.remove();

            // remove id from visiblePanelIds
            this.visiblePanelIds = this.visiblePanelIds.filter(id => id !== panel_id);

            // panel.destroy();
            const removedPanels = this._panels.splice(i, 1);
            return removedPanels[0];
        }

        return null;
    }

    /** @brief Removes panels with ids from passed array.
     * @param     {string[]} panel_ids Identifiers of panels we'd like to remove.
     * @returns   {this} Returns PanelContainer instance to allow chaining.
     */
    removePanels(panel_ids: readonly string[]): this {
        panel_ids.forEach(id => {
            this.removePanel(id);
        });

        return this;
    }

    /** @brief Shows some list of panels, hides others.
     * @param     {string[]} panel_ids Identifiers of panels to show.
     * @returns  {this} Returns PanelContainer instance to allow chaining.
     */
    showPanels(panel_ids: (string | [string, boolean])[]): this {
        this.visiblePanelIds = [];

        for (const panel_id of panel_ids) {
            let id: string;
            let visible: boolean;
            if (Array.isArray(panel_id)) { 
                id = panel_id[0];
                visible = panel_id[1]; // could be temporary hidden
            }
            else {
                id = panel_id;
                visible = true;
            }

            this.visiblePanelIds.push(id)

            const panel = this._panels.find(p => p.id === id);

            if (!panel) {
                console.warn(`Panel with ID "${panel_id}" was not found.`);
                continue;
            }

            if (!visible) panel.hide(); else
            panel.show();

            this.content.appendChild(panel.panelElement);
        }


        // hide all panels not in visiblePanelIds
        this._panels.forEach(p => {
            if (!this.visiblePanelIds.includes(p.id))
                p.hide();
        });


        return this;
    }

    /** @brief Shows only temporary panels, hides all others.
     * @param     {string[]} temporary_panel_ids Identifiers of panels we'd like to switch to temporary mode.
     * @returns   {this} Returns PanelContainer instance to allow chaining.
     */
    enableTemporaryMode(temporary_panel_ids: string[]): this {
        // hide all panels not in visiblePanelIds
        this._panels.forEach(p => {
            if (!temporary_panel_ids.includes(p.id))
                p.hide();
        });

        for (const panel_id of temporary_panel_ids) {
            const panel = this._panels.find(p => p.id === panel_id);

            if (!panel) {
                console.warn(`Panel with ID "${panel_id}" was not found.`);
                continue;
            }

            panel.enableTemporaryMode();
            panel.show();
            this.content.appendChild(panel.panelElement);
        }

        return this;
    }

    /** @brief Hides temporary panels, shows others (according to their show/hide states before switching to temporary mode).
     * @returns   {this} Returns PanelContainer instance to allow chaining.
     */
    disableTemporaryMode(): this {
        // hide all panels not in visiblePanelIds
        this._panels.forEach(p => {
            p.disableTemporaryMode();
            if (!this.visiblePanelIds.includes(p.id))
                p.hide();
        });

        for (const panel_id of this.visiblePanelIds) {
            const panel = this._panels.find(p => p.id === panel_id);

            if (!panel) {
                console.warn(`Panel with ID "${panel_id}" was not found.`);
                continue;
            }

            panel.show();
            this.content.appendChild(panel.panelElement);
        }

        return this;
    }
}

export { PanelContainer };
